/* eslint-disable */
// Place at: src/components/PhoneInput.js
// Requires: country-codes-list, react-native-svg (you already use SVGs)
import React, {forwardRef, useMemo, useState} from 'react';
import {
  FlatList,
  Image,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Svg, {Circle, Line, Path} from 'react-native-svg';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import * as countryCodesLib from 'country-codes-list';
import {colors, fonts} from '../config/theme';

const countryCodes = countryCodesLib.default || countryCodesLib;

export const COUNTRIES = countryCodes
  .all()
  .map(c => ({
    code: c.countryCode,
    name: c.countryNameEn,
    dial: String(c.countryCallingCode),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const DEFAULT_COUNTRY =
  COUNTRIES.find(c => c.code === 'NL') || COUNTRIES[0];

// ---------- Icons (SVG, so they look the same on every phone) ----------
const Icon = ({name, size = 16, color = colors.textMuted}) => {
  const common = {
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    fill: 'none',
  };
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      {name === 'chevron' && <Path d="M6 9l6 6 6-6" {...common} />}
      {name === 'close' && <Path d="M6 6l12 12M18 6L6 18" {...common} />}
      {name === 'check' && <Path d="M5 12.5l4.5 4.5L19 7.5" {...common} />}
      {name === 'search' && (
        <>
          <Circle cx="11" cy="11" r="6.5" {...common} />
          <Line x1="16" y1="16" x2="20.5" y2="20.5" {...common} />
        </>
      )}
    </Svg>
  );
};

// ---------- Flag (image, not emoji) ----------
// Emoji flags don't render on many phones (older Android, some OEM skins),
// so we load a small PNG flag. If it can't load (offline), the country code
// is shown instead.
const Flag = ({code, width = 28, height = 20}) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <View style={[styles.flagFallback, {width, height}]}>
        <Text style={styles.flagFallbackText}>{code}</Text>
      </View>
    );
  }
  return (
    <Image
      source={{uri: `https://flagcdn.com/w80/${code.toLowerCase()}.png`}}
      style={{width, height, borderRadius: 3}}
      resizeMode="cover"
      onError={() => setFailed(true)}
    />
  );
};

const PhoneInput = forwardRef(
  (
    {
      label,
      placeholder,
      value,
      onChangeText,
      error,
      country,
      onCountryChange,
      ...rest
    },
    ref,
  ) => {
    const insets = useSafeAreaInsets();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const data = useMemo(() => {
      const q = query.trim().toLowerCase().replace(/^\+/, '');
      if (!q) {
        return COUNTRIES;
      }
      return COUNTRIES.filter(
        c =>
          c.name.toLowerCase().includes(q) ||
          c.code.toLowerCase() === q ||
          c.dial.startsWith(q),
      );
    }, [query]);

    const close = () => {
      setOpen(false);
      setQuery('');
    };

    const select = item => {
      onCountryChange(item);
      close();
    };

    return (
      <View>
        {!!label && <Text style={styles.label}>{label}</Text>}

        <View style={[styles.row, !!error && styles.rowError]}>
          <Pressable
            style={styles.codeBtn}
            onPress={() => setOpen(true)}
            accessibilityRole="button"
            accessibilityLabel="Select country code">
            <Flag code={country.code} />
            <Text style={styles.dial}>+{country.dial}</Text>
            <Icon name="chevron" size={14} />
          </Pressable>
          <View style={styles.divider} />
          <TextInput
            ref={ref}
            style={styles.input}
            placeholder={placeholder}
            placeholderTextColor={colors.textMuted}
            value={value}
            onChangeText={onChangeText}
            keyboardType="phone-pad"
            {...rest}
          />
        </View>
        {!!error && <Text style={styles.error}>{error}</Text>}

        <Modal
          visible={open}
          animationType="slide"
          transparent
          onRequestClose={close}>
          <Pressable style={styles.backdrop} onPress={close} />
          <View style={[styles.sheet, {paddingBottom: insets.bottom}]}>
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Select country</Text>
              <Pressable onPress={close} hitSlop={12}>
                <Icon name="close" size={20} />
              </Pressable>
            </View>

            <View style={styles.searchWrap}>
              <Icon name="search" size={18} />
              <TextInput
                style={styles.search}
                placeholder="Search country or code"
                placeholderTextColor={colors.textMuted}
                value={query}
                onChangeText={setQuery}
                autoCorrect={false}
              />
            </View>

            <FlatList
              data={data}
              keyExtractor={item => item.code}
              keyboardShouldPersistTaps="handled"
              initialNumToRender={20}
              renderItem={({item}) => {
                const active = item.code === country.code;
                return (
                  <Pressable
                    style={[styles.item, active && styles.itemActive]}
                    onPress={() => select(item)}>
                    <View style={styles.itemFlag}>
                      <Flag code={item.code} />
                    </View>
                    <Text style={styles.itemName} numberOfLines={1}>
                      {item.name}
                    </Text>
                    <Text style={styles.itemDial}>+{item.dial}</Text>
                    <View style={styles.itemCheck}>
                      {active && (
                        <Icon name="check" size={16} color={colors.primary} />
                      )}
                    </View>
                  </Pressable>
                );
              }}
            />
          </View>
        </Modal>
      </View>
    );
  },
);

const BORDER = '#E3E8EF';

const styles = StyleSheet.create({
  label: {
    fontFamily: fonts.regular,
    fontSize: 12,
    color: colors.textEmphasis,
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 52,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: '#F7F9FB',
  },
  rowError: {borderColor: '#D93025'},
  codeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    height: '100%',
  },
  dial: {
    fontFamily: fonts.bold,
    fontSize: 14,
    color: colors.textEmphasis,
  },
  divider: {width: 1, height: 24, backgroundColor: BORDER},
  input: {
    flex: 1,
    paddingHorizontal: 12,
    fontFamily: fonts.regular,
    fontSize: 14,
    color: colors.textEmphasis,
  },
  error: {
    marginTop: 6,
    fontFamily: fonts.regular,
    fontSize: 12,
    color: '#D93025',
  },

  flagFallback: {
    borderRadius: 3,
    backgroundColor: BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  flagFallbackText: {
    fontFamily: fonts.bold,
    fontSize: 9,
    color: colors.textMuted,
  },

  backdrop: {flex: 1, backgroundColor: 'rgba(0,0,0,0.4)'},
  sheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '75%',
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 16,
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  sheetTitle: {
    fontFamily: fonts.bold,
    fontSize: 16,
    color: colors.textEmphasis,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 20,
    marginBottom: 8,
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: '#F7F9FB',
    paddingHorizontal: 14,
  },
  search: {
    flex: 1,
    height: '100%',
    padding: 0,
    fontFamily: fonts.regular,
    fontSize: 14,
    color: colors.textEmphasis,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  itemActive: {backgroundColor: '#EAF3EF'},
  itemFlag: {marginRight: 12},
  itemName: {
    flex: 1,
    fontFamily: fonts.regular,
    fontSize: 14,
    color: colors.textEmphasis,
  },
  itemDial: {
    fontFamily: fonts.bold,
    fontSize: 14,
    color: colors.textMuted,
  },
  itemCheck: {width: 24, alignItems: 'flex-end'},
});

export default PhoneInput;